import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, CalendarDays } from 'lucide-react';
import NormsModal from '@/components/NormsModal';
import { ACTIVITY_NORMS, FREE_ACCESS_NORMS } from '@/data/activityNorms';

const ACTIVITIES = [
  // ── Reservables (requieren registro) ─────────────────────────────────────
  {
    id: 'el-club-de-manri',
    title: 'El Club de Manri',
    subtitle: 'Zona interior · Ludoteca',
    type: 'reservable',
    schedule: 'Lun–Dom · 10:30–11:30 / 11:45–12:45 / 15:00–16:00',
    conditions: '3–13 años · previo registro · menores de 5 acompañados',
    normsKey: 'El Club de Manri',
    normsSource: 'activity',
  },
  {
    id: 'minigolf',
    title: 'Minigolf',
    subtitle: 'Zona exterior · Circuito de 9 hoyos',
    type: 'reservable',
    schedule: 'Lun–Dom · 10:30–13:00 / 15:00–17:30',
    conditions: 'Todas las edades · con registro · menores de 8 acompañados',
    normsKey: 'Minigolf',
    normsSource: 'activity',
  },
  // ── Acceso libre ──────────────────────────────────────────────────────────
  {
    id: 'castillo-acuatico',
    title: 'Castillo Acuático',
    subtitle: 'Piscina de La Cascada',
    type: 'free',
    schedule: 'Lun–Dom · 11:00–17:00',
    conditions: '5–13 años · acceso libre · saber nadar obligatorio',
    normsKey: 'castillo-acuatico',
    normsSource: 'free',
  },
  {
    id: 'castillo-terrestre',
    title: 'Castillo Terrestre',
    subtitle: 'Zona exterior · Temática pirata',
    type: 'free',
    schedule: 'Lun–Dom · 10:30–13:00 / 15:00–17:30',
    conditions: '3–13 años · acceso libre · menores de 7 con adulto',
    normsKey: 'castillo-terrestre',
    normsSource: 'free',
  },
  {
    id: 'piraguas',
    title: 'Piraguas · Kayak',
    subtitle: 'Piscina de La Cascada',
    type: 'free',
    schedule: 'Mar–Sáb · 13:00–15:00',
    conditions: '5–13 años · acceso libre · saber nadar obligatorio',
    normsKey: 'piraguas',
    normsSource: 'free',
  },
  {
    id: 'aquagym',
    title: 'Aquagym',
    subtitle: 'Piscina principal',
    type: 'free',
    schedule: 'Lun–Dom · 13:00–14:00',
    conditions: 'Todas las edades · acceso libre',
    normsKey: null,
    normsSource: null,
  },
  {
    id: 'animacion',
    title: 'Animación y juegos',
    subtitle: 'Piscina infantil',
    type: 'free',
    schedule: 'Lun–Sáb · 16:00–17:30 · Dom 15:30–18:00',
    conditions: '3–13 años · acceso libre',
    normsKey: null,
    normsSource: null,
  },
];

function getNorms(act) {
  if (!act.normsKey) return null;
  return act.normsSource === 'activity'
    ? ACTIVITY_NORMS[act.normsKey]
    : FREE_ACCESS_NORMS[act.normsKey];
}

export default function ActivitiesPage() {
  const navigate = useNavigate();
  const [normsOpen, setNormsOpen] = useState(null); // { title, norms }

  return (
    <div className="min-h-screen bg-[#FBF8F3]">
      {/* Header */}
      <header className="bg-white border-b border-black/5 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-[#FBF8F3] text-manrique-grey hover:text-manrique-black transition-colors"
            aria-label="Volver"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            <CalendarDays size={20} className="text-[#A8752B]" />
            <h1 className="font-heading font-bold text-lg text-[#48293A]">Actividades y horarios</h1>
          </div>
          <img src="/logo-mark.png" alt="El Club de Manri" className="h-8 w-auto ml-auto opacity-70" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        <p className="text-sm text-manrique-muted text-center px-2">
          Todos los horarios son orientativos y pueden modificarse. Consulta con el monitor de cada actividad.
        </p>

        {/* Actividades reservables */}
        <div>
          <h2 className="text-xs font-bold uppercase tracking-widest text-manrique-muted mb-3 px-1">Con registro previo</h2>
          <div className="space-y-4">
            {ACTIVITIES.filter(a => a.type === 'reservable').map(act => {
              const norms = getNorms(act);
              return <ActivityCard key={act.id} act={act} norms={norms} onOpenNorms={setNormsOpen} />;
            })}
          </div>
        </div>

        {/* Actividades libres */}
        <div className="pt-2">
          <h2 className="text-xs font-bold uppercase tracking-widest text-manrique-muted mb-3 px-1">Acceso libre (sin reserva)</h2>
          <div className="space-y-4">
            {ACTIVITIES.filter(a => a.type === 'free').map(act => {
              const norms = getNorms(act);
              return <ActivityCard key={act.id} act={act} norms={norms} onOpenNorms={setNormsOpen} />;
            })}
          </div>
        </div>
      </main>

      <NormsModal
        open={!!normsOpen}
        onClose={() => setNormsOpen(null)}
        title={normsOpen?.title}
        norms={normsOpen?.norms}
      />
    </div>
  );
}

function ActivityCard({ act, norms, onOpenNorms }) {
  const isReservable = act.type === 'reservable';
  const headerGradient = isReservable
    ? 'bg-gradient-to-br from-[#48293A] to-[#844E33]'
    : 'bg-gradient-to-br from-[#27AE60] to-[#1E8C4E]';

  return (
    <div className="rounded-3xl overflow-hidden border border-black/5 shadow-sm bg-white">
      {/* Cabecera de color */}
      <div className={`${headerGradient} px-5 py-4`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-white/20 text-white">
              {isReservable ? 'Registro obligatorio' : 'Acceso libre'}
            </span>
            <h3 className="font-heading font-bold text-xl text-white mt-2">{act.title}</h3>
            <p className="text-white/70 text-xs mt-0.5">{act.subtitle}</p>
          </div>
          {norms && (
            <button
              onClick={() => onOpenNorms({ title: act.title, norms })}
              className="flex-shrink-0 flex items-center gap-1.5 text-xs font-semibold text-white bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-full transition-colors whitespace-nowrap"
            >
              <BookOpen size={13} /> Ver normas
            </button>
          )}
        </div>
      </div>

      {/* Cuerpo */}
      <div className="px-5 py-4 space-y-3">
        {/* Horario */}
        <p className="text-sm font-medium text-manrique-black">{act.schedule}</p>
        <p className="text-xs text-manrique-grey">{act.conditions}</p>

        {/* Stats chips */}
        {norms?.stats && (
          <div className="flex flex-wrap gap-1.5">
            {norms.stats.map(s => (
              <span key={s.label} className="text-[10px] bg-black/5 text-manrique-black px-2 py-0.5 rounded-full">
                {s.label}: <strong>{s.value}</strong>
              </span>
            ))}
          </div>
        )}

        {/* Reglas clave */}
        {norms?.rules && (
          <ul className="space-y-1">
            {norms.rules.slice(0, 4).map((r, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-manrique-grey">
                <span className={`mt-0.5 flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center text-white text-[9px] font-bold ${r.ok ? 'bg-[#27AE60]' : 'bg-[#E74C3C]'}`}>
                  {r.ok ? '✓' : '✗'}
                </span>
                {r.text}
              </li>
            ))}
          </ul>
        )}

        {norms?.footer && (
          <p className="text-[11px] text-manrique-muted italic border-t border-black/5 pt-2">{norms.footer}</p>
        )}
      </div>
    </div>
  );
}
